/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ConfigItemRow from './ConfigItemRow';
import {
  tabDirectionType,
  specConfigItemStatus,
  sinteringSizePath,
  qcSinteringManualResult,
} from '../../../../constants/enums';
import useStyles from './useStyles';
import useCommonStyles from '../../useStyles';
import Typography from '../../../../components/Typography/Typography';
import Grid from '../../../../components/Grid/Grid';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import PaperContainer from '../../../../components/PaperContainer/PaperContainer';
import Button from '../../../../components/Button/Button';
import InputField from '../../../../components/InputField/InputField';
import Radio from '../../../../components/Radio/Radio';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import Switch from '../../../../components/Switch/Switch';
import {
  calculateTableIndexesByItem,
  calculateTableIndexesBySample,
} from '../../../../Utils/arrayUtil';

/**
 *
 * @param {Array<{specId: string, cpk: string}>} cpkResults - 前次cpk計算結果
 * @param {Array<{specId: string, specItem: string, specTarget: string, specUpper: string, specLower: string, specBias: string}>} specConfig - 量測項目設定
 * @param {Array<{rowId: string, specItems: Array<{specId: string, value: string}>}>} renderRow - 量測數據
 * @param {number} rowLength - 量測數量
 * @param {boolean} otherIsDone - 其他量測項目是否完成
 * @param {boolean} foldingRow - 是否展開
 * @param {boolean} showTable - 是否顯示表格
 * @param {boolean} isLoading - 是否正在載入
 * @returns
 */
const QCSinteringSize = ({
  path,
  showTable,
  isLoading,
  foldingRow,
  toggleFoldingRow,
  warningMsg,
  specConfig,
  setSpecConfig,
  renderRow,
  // transDefectTypes,
  setRenderRow,
  resultNote,
  handleResultNoteChange,
  handleFormSubmit,
  rowLength,
  enableDelete,
  handleDelete,
  disabled,
  otherIsDone,
  cpkResults,
  sinterManualResult,
  setSinterManualResult,
  originalSinterManualResult,
}) => {
  const tableEl = useRef(null);
  const scrollTarget = useRef(null);
  const [tabIndexTable, setTabIndexTable] = useState([]);
  const [sinteringSizeManualResult, setSinteringSizeManualResult] = useState(sinterManualResult);
  const [outSpecResultList, setOutSpecResultList] = useState([]);
  const { classes } = useStyles();
  const commonClasses = useCommonStyles().classes;
  const [onScroll, setOnScroll] = useState(false);
  const scrollObject = {
    position: 'sticky',
    top: '75px',
    backgroundColor: '#fff',
    opacity: '1',
    zIndex: '10',
    margin: '0 auto',
    alignItem: 'middle',
    padding: '.5em 1.5rem 1rem 1.5rem',
  };

  const [actionBasis, setActionBasis] = useState(tabDirectionType.BY_ITEM);

  const handleToggleActiveSpec = ({ checked, specId }) => {
    const newSpecConfig = specConfig.map((item) => {
      if (item.specId === specId) {
        return { ...item, isActive: checked };
      }
      return item;
    });
    setSpecConfig(newSpecConfig);
    // 如果取消啟用量測項目，則將outSpecResultList中該項目的結果移除
    if (!checked) {
      setOutSpecResultList((prev) => prev.filter((targetId) => targetId.split('-')[1] !== specId));
    }
  };

  const onActionBasisChange = (e) => {
    setActionBasis(e.target.value);
  };

  const onSinteringSizeManualResultChange = (e) => {
    setSinteringSizeManualResult(e.target.value === 'true');
    setSinterManualResult(e.target.value === 'true');
  };

  const handleInputBlur = (isOutSpecResult, targetId) => {
    // 只有在尚未有手動結果時，才會更新手動結果，一旦有過手動結果，就不會再更新手動結果
    if (originalSinterManualResult === null) {
      let outSpecResultListLength = outSpecResultList.length;
      if (isOutSpecResult) {
        if (outSpecResultList.includes(targetId)) return;
        setOutSpecResultList((prev) => {
          const newOutSpecResultList = [...prev, targetId];
          outSpecResultListLength = newOutSpecResultList.length;
          return newOutSpecResultList;
        });
      } else if (outSpecResultList.includes(targetId)) {
        setOutSpecResultList((prev) => {
          const newOutSpecResultList = prev.filter((id) => id !== targetId);
          outSpecResultListLength = newOutSpecResultList.length;
          return newOutSpecResultList;
        });
      }
      setSinteringSizeManualResult(outSpecResultListLength === 0);
      setSinterManualResult(outSpecResultListLength === 0);
    }
  };

  const handleClearRow = (targetRowId) => {
    // 清除row時，清除outSpecResultList中對應的rowId
    setOutSpecResultList((prev) => {
      const newOutSpecResultList = prev.filter((id) => id.split('-')[0] !== targetRowId.toString());
      return newOutSpecResultList;
    });
  };

  const handleFocusChange = (e) => {
    const minTabIndex = 1;
    const currentTabIndex = e.target.tabIndex;
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      let prevTabIndex = 0;
      if (currentTabIndex === minTabIndex) {
        prevTabIndex = currentTabIndex;
      } else {
        prevTabIndex = currentTabIndex - 1;
      }
      const prevEl = document.querySelectorAll(`[tabindex = "${prevTabIndex}"]`)[1];
      if (!prevEl) return;
      prevEl.focus();
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const nextTabIndex = currentTabIndex + 1;
      const nextEl = document.querySelectorAll(`[tabindex="${nextTabIndex}"]`)[1];
      if (!nextEl) return;
      nextEl.focus();
    }
  };

  useEffect(() => {
    if (showTable) {
      document.addEventListener('scroll', () => {
        if (!scrollTarget.current) return;
        const position = scrollTarget.current.getBoundingClientRect();
        if (window.pageYOffset >= position.bottom + position.height * 2) {
          setOnScroll(true);
          return;
        }
        setOnScroll(false);
      });
    }
    if (showTable && tableEl.current) {
      tableEl.current.addEventListener('keydown', handleFocusChange);
    }

    return () => {
      if (!tableEl.current) return;
      tableEl.current.removeEventListener('keydown', handleFocusChange);
    };
  }, [showTable]);

  useEffect(() => {
    const inactiveIndexes = specConfig.reduce((acc, item, index) => {
      if (!item.isActive) {
        acc.push(index);
      }
      return acc;
    }, []);
    const removedIndexes = specConfig.reduce((acc, item, index) => {
      if (item.colStatus === specConfigItemStatus.REMOVED) {
        acc.push(index);
      }
      return acc;
    }, []);
    // 將inactiveIndexes和removedIndexes合併，並去除重複項目
    const indexes = [...inactiveIndexes, ...removedIndexes].filter(
      (value, index, self) => self.indexOf(value) === index
    );
    setTabIndexTable(
      actionBasis === tabDirectionType.BY_ITEM
        ? calculateTableIndexesByItem({
            disabledCols: indexes,
            rows: rowLength,
            cols: specConfig.length,
          })
        : calculateTableIndexesBySample({
            disabledCols: indexes,
            rows: rowLength,
            cols: specConfig.length,
          })
    );
  }, [specConfig, actionBasis]);

  useEffect(() => {
    setSinteringSizeManualResult(sinterManualResult);
  }, [sinterManualResult]);

  return (
    <>
      {!showTable && isLoading && <CircularProgress mt={7.5} />}
      {showTable && onScroll && (
        // eslint-disable-next-line react/jsx-boolean-value
        <PaperContainer positionFixed={true} scrollObject={scrollObject}>
          <Grid xs={12} container className={classes.table} ref={tableEl}>
            <ul className={commonClasses.table_header}>
              <li
                className={`${commonClasses.table_column} ${commonClasses.onScroll_table_column}`}
              >
                <div className={`${commonClasses.table_cell} ${classes.table_action_cell}`}>
                  <Typography variant="subtitle2" />
                </div>
              </li>
              <li className={commonClasses.table_column}>
                <div className={`${commonClasses.table_cell} ${classes.table_action_cell}`}>
                  <Typography variant="subtitle2" />
                </div>
              </li>
              {specConfig.map((item) => (
                <li
                  className={`${commonClasses.table_column} ${classes.table_column} ${classes.table_header}`}
                  key={item.specId}
                >
                  <div className={`${commonClasses.table_cell} ${classes.table_cell}`}>
                    <Typography variant="subtitle2">{item.specItem}</Typography>
                    <div className={classes.spec_group}>
                      <div className={classes.target_group}>
                        <Typography variant="h5" className={classes.target_value}>
                          {item.specTarget}
                        </Typography>
                        <div className={classes.limit}>
                          <span className={`${classes.upper} ${classes.target}`}>
                            +{item.specUpper}
                          </span>
                          <span className={`${classes.lower} ${classes.target}`}>
                            {item.specLower}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {item.specBias.length > 0 && (
                    <Typography variant="body2" className={classes.bias}>
                      量測偏差：{' '}
                      {parseInt(item.specBias, 10) > 0 ? `+${item.specBias}` : item.specBias}
                    </Typography>
                  )}
                </li>
              ))}
              <li className={commonClasses.table_column}>
                <div className={`${commonClasses.table_cell} ${classes.defect_type_cell}`}>
                  <Typography variant="subtitle2" />
                </div>
              </li>
            </ul>
          </Grid>
        </PaperContainer>
      )}
      {showTable && (
        <PaperContainer className={commonClasses.container}>
          <Typography variant="h6">填寫量測數據</Typography>
          <Grid container className={commonClasses.content} direction="column">
            <Grid xs={12} container justifyContent="space-between">
              <Radio
                label="依量測item填寫"
                name="byItem"
                value={tabDirectionType.BY_ITEM}
                selectedValue={actionBasis}
                onChange={onActionBasisChange}
              />
              <Radio
                label="依量測sample填寫"
                name="bySample"
                value={tabDirectionType.BY_SAMPLE}
                selectedValue={actionBasis}
                onChange={onActionBasisChange}
              />
              {foldingRow && (
                <Button
                  variant="outlined"
                  // color="grey"
                  customClasses={classes.unfold_btn}
                  rounded
                  endIcon={<ExpandMoreRoundedIcon />}
                  onClick={toggleFoldingRow}
                >
                  全部展開
                </Button>
              )}
              {!foldingRow && (
                <Button
                  variant="outlined"
                  // color="grey"
                  customClasses={`${classes.unfold_btn} active`}
                  rounded
                  endIcon={<ExpandLessRoundedIcon />}
                  onClick={toggleFoldingRow}
                >
                  全部收闔
                </Button>
              )}
            </Grid>
            <Snackbar
              msg={warningMsg}
              open={warningMsg.length > 0}
              type="error"
              className={commonClasses.error_snackbar}
            />
            <Grid xs={12} container className={classes.table} ref={tableEl}>
              <ul className={classes.table_sub_header} ref={scrollTarget}>
                <li className={commonClasses.table_column}>
                  <div
                    className={`${commonClasses.table_cell} ${classes.table_placeholder_cell}`}
                  />
                </li>
                {specConfig.map((item) => (
                  <li
                    className={`${commonClasses.table_column} ${classes.table_column} ${classes.table_header}`}
                    key={item.specId}
                  >
                    <div className={`${commonClasses.table_cell} ${classes.table_cell}`}>
                      <Switch
                        label="啟用量測項目"
                        name={item.specId}
                        color="success"
                        checked={
                          item.colStatus === specConfigItemStatus.REMOVED ? false : item.isActive
                        }
                        onChange={(e) =>
                          handleToggleActiveSpec({
                            checked: e.target.checked,
                            specId: item.specId,
                          })
                        }
                        disabled={item.colStatus === specConfigItemStatus.REMOVED}
                      />
                    </div>
                  </li>
                ))}
                <li className={commonClasses.table_column}>
                  <div className={`${commonClasses.table_cell} ${classes.defect_type_cell}`} />
                </li>
              </ul>
              <ul className={commonClasses.table_header} ref={scrollTarget}>
                <li className={commonClasses.table_column}>
                  <div className={`${commonClasses.table_cell} ${classes.table_action_cell}`}>
                    <Typography variant="subtitle2">動作</Typography>
                  </div>
                </li>
                <li className={commonClasses.table_column}>
                  <div className={`${commonClasses.table_cell} ${classes.table_action_cell}`}>
                    <Typography variant="subtitle2">序號</Typography>
                  </div>
                </li>
                {specConfig.map((item) => (
                  <li
                    className={`${commonClasses.table_column} ${classes.table_column} ${classes.table_header}`}
                    key={item.specId}
                  >
                    <div className={`${commonClasses.table_cell} ${classes.table_cell}`}>
                      <Typography variant="subtitle2">{item.specItem}</Typography>
                      <div className={classes.spec_group}>
                        <div className={classes.target_group}>
                          <Typography variant="h5" className={classes.target_value}>
                            {item.specTarget}
                          </Typography>
                          <Typography variant="h5" className={`${classes.upper} ${classes.target}`}>
                            +{item.specUpper}
                          </Typography>
                          <Typography variant="h5" className={`${classes.lower} ${classes.target}`}>
                            {item.specLower}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    {item.specBias.length > 0 && (
                      <Typography variant="body2" className={classes.bias}>
                        量測偏差：{' '}
                        {parseInt(item.specBias, 10) > 0 ? `+${item.specBias}` : item.specBias}
                      </Typography>
                    )}
                  </li>
                ))}
                <li className={commonClasses.table_column}>
                  <div className={`${commonClasses.table_cell} ${classes.defect_type_cell}`}>
                    <Typography variant="subtitle2">Defect Type</Typography>
                  </div>
                </li>
              </ul>
              {renderRow.map((item, index) => (
                <ConfigItemRow
                  path={path}
                  data={item}
                  handleRowChange={setRenderRow}
                  foldingAllRows={foldingRow}
                  specConfig={specConfig}
                  rowId={item.rowId}
                  key={item.rowId}
                  index={index}
                  initialRowState={{ specItems: item.specItems, defectType: item.defectType }}
                  tabDirection={actionBasis}
                  rowLength={rowLength}
                  otherIsDone={otherIsDone}
                  tabIndexRow={tabIndexTable[index]}
                  onInputBlur={handleInputBlur}
                  onClearRow={handleClearRow}
                />
              ))}
            </Grid>
          </Grid>
          {/* 20250602 Shane - cpk table - 燒結尺寸檢測、有前一次cpk計算結果才顯示 */}
          {path === sinteringSizePath.SIZE && renderRow.length >= 32 && cpkResults.length > 0 && (
            <div className={commonClasses.table_header}>
              <div className={`${commonClasses.table_column}`}>
                <div className={`${commonClasses.table_cell} ${classes.table_cpk_title_cell}`}>
                  <Typography variant="subtitle2">前次cpk計算結果</Typography>
                </div>
              </div>
              {cpkResults.map((item) => {
                const spec = specConfig.find((specItem) => specItem.specId === item.specId);
                if (!spec) return null;
                return (
                  <div
                    className={`${commonClasses.table_column} ${classes.table_column} ${classes.table_cpk_cell}`}
                    key={item.specId}
                  >
                    <Typography variant="subtitle2">{item.cpk}</Typography>
                  </div>
                );
              })}
              <div className={commonClasses.table_column}>
                <div className={`${commonClasses.table_cell} ${classes.defect_type_cell}`} />
              </div>
            </div>
          )}
          {path === sinteringSizePath.SIZE && (
            <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }} gap={2}>
              <Typography variant="subtitle1" sx={{ pr: 1 }}>
                輸入量測結果：
              </Typography>
              <Radio
                label="通過"
                name="resultPass"
                value={qcSinteringManualResult.PASS}
                selectedValue={sinteringSizeManualResult}
                onChange={onSinteringSizeManualResultChange}
                className={classes.result_item}
                disabled={disabled}
              />
              <Radio
                label="不通過"
                name="resultFail"
                value={qcSinteringManualResult.FAIL}
                selectedValue={sinteringSizeManualResult}
                onChange={onSinteringSizeManualResultChange}
                disabled={disabled}
              />
            </Grid>
          )}
          <Grid container alignItems="center" wrap="nowrap" className={classes.table_footer}>
            <InputField
              id="note"
              name="note"
              type="text"
              label="備註"
              placeholder="請輸入備註內容"
              value={resultNote}
              onChange={handleResultNoteChange}
              disabled={otherIsDone}
            />
            {/* 20230103 Shane - Delete QC record functionality will re-implement in the next phase */}
            {enableDelete && (
              <Button
                variant="contained"
                color="error"
                rounded
                customClasses={classes.submit_btn}
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={disabled}
              >
                刪除
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              rounded
              customClasses={classes.submit_btn}
              startIcon={<CheckRoundedIcon />}
              onClick={handleFormSubmit}
              disabled={isLoading || otherIsDone}
            >
              送出
            </Button>
          </Grid>
        </PaperContainer>
      )}
    </>
  );
};

QCSinteringSize.defaultProps = {
  cpkResults: [],
  originalSinterManualResult: null,
};

QCSinteringSize.propTypes = {
  path: PropTypes.string.isRequired,
  showTable: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  foldingRow: PropTypes.bool.isRequired,
  toggleFoldingRow: PropTypes.func.isRequired,
  warningMsg: PropTypes.string.isRequired,
  specConfig: PropTypes.array.isRequired,
  setSpecConfig: PropTypes.func.isRequired,
  renderRow: PropTypes.array.isRequired,
  // transDefectTypes: PropTypes.array.isRequired,
  setRenderRow: PropTypes.func.isRequired,
  resultNote: PropTypes.string.isRequired,
  handleResultNoteChange: PropTypes.func.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  rowLength: PropTypes.number.isRequired,
  handleDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  enableDelete: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  otherIsDone: PropTypes.bool,
  cpkResults: PropTypes.arrayOf(PropTypes.object),
  sinterManualResult: PropTypes.bool.isRequired,
  setSinterManualResult: PropTypes.func.isRequired,
  originalSinterManualResult: PropTypes.bool,
};

export default QCSinteringSize;
